import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from './Button';

interface DialogContextType {
    showDialog: (options: DialogOptions) => void;
    closeDialog: () => void;
}

interface DialogOptions {
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
    onConfirm?: () => Promise<void> | void;
    onCancel?: () => void;
    loading?: boolean;
    content?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [dialog, setDialog] = useState<DialogOptions | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (dialog) {
            previousActiveElement.current = document.activeElement as HTMLElement;
            confirmButtonRef.current?.focus();
            document.body.style.overflow = 'hidden';
            requestAnimationFrame(() => {
                setIsOpen(true);
            });
        } else {
            document.body.style.overflow = '';
            previousActiveElement.current?.focus();
        }
    }, [dialog]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && dialog && !isLoading) {
                handleCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [dialog, isLoading]);

    const showDialog = useCallback((options: DialogOptions) => {
        setDialog({ size: 'md', ...options });
        setIsClosing(false);
        setIsLoading(false);
        setIsOpen(false);
    }, []);

    const closeDialog = useCallback(() => {
        if (!isLoading) {
            setIsClosing(true);
            setIsOpen(false);
            setTimeout(() => {
                setDialog(null);
                setIsClosing(false);
            }, 300);
        }
    }, [isLoading]);

    const handleConfirm = async () => {
        if (isLoading || !dialog?.onConfirm) return;

        try {
            setIsLoading(true);
            await dialog.onConfirm();
            closeDialog();
        } catch (error) {
            console.error('Dialog confirmation error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            dialog?.onCancel?.();
            closeDialog();
        }
    };

    const getDialogSize = () => {
        switch (dialog?.size) {
            case 'sm':
                return 'sm:max-w-md';
            case 'lg':
                return 'sm:max-w-3xl';
            default:
                return 'sm:max-w-xl';
        }
    };

    const dialogContent = dialog && (
        <div
            className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'
                }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
        >
            <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
                <div
                    className={`fixed inset-0 bg-dark-900/75 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'
                        }`}
                    aria-hidden="true"
                    onClick={handleCancel}
                />

                <div className="overflow-y-auto fixed inset-0 z-10">
                    <div className="flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0">
                        <div
                            ref={dialogRef}
                            className={`relative w-full ${getDialogSize()} transform overflow-hidden rounded-lg bg-dark-800 border border-dark-700 text-left shadow-xl transition-all duration-300 ${isOpen
                                ? 'opacity-100 translate-y-0 sm:scale-100'
                                : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                                }`}
                            role="document"
                        >
                            <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3
                                                id="dialog-title"
                                                className="text-lg font-medium leading-6 text-dark-100"
                                            >
                                                {dialog.title}
                                            </h3>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon={X}
                                                onClick={handleCancel}
                                                disabled={isLoading}
                                                aria-label="Close dialog"
                                            />
                                        </div>
                                        {dialog.description && (
                                            <div className="mt-2">
                                                <p className="text-sm text-dark-300">{dialog.description}</p>
                                            </div>
                                        )}
                                        {dialog.content && (
                                            <div className="mt-4">
                                                {dialog.content}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="px-4 py-3 bg-dark-900/50 sm:flex sm:flex-row-reverse sm:px-6">
                                <Button
                                    ref={confirmButtonRef}
                                    variant={dialog.variant === 'danger' ? 'danger' : 'primary'}
                                    onClick={handleConfirm}
                                    disabled={isLoading}
                                    loading={isLoading}
                                    className="w-full sm:w-auto sm:ml-2"
                                >
                                    {dialog.confirmLabel || 'Confirm'}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleCancel}
                                    disabled={isLoading}
                                    className="mt-3 w-full sm:mt-0 sm:w-auto"
                                >
                                    {dialog.cancelLabel || 'Cancel'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <DialogContext.Provider value={{ showDialog, closeDialog }}>
            {children}
            {dialog && createPortal(dialogContent, document.body)}
        </DialogContext.Provider>
    );
};